import { ref, watch, onMounted, onUnmounted } from 'vue';

export interface ColumnDef {
  key: string;
  defaultWidth: number;
  minWidth: number;
}

export function useResizableColumns(columns: ColumnDef[], storageKey?: string) {
  const columnWidths = ref<Record<string, number>>({});
  const resizing = ref<string | null>(null);

  let startX = 0;
  let startWidth = 0;

  // Load from localStorage or use defaults
  function loadWidths() {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          columnWidths.value = JSON.parse(saved);
          return;
        } catch {
          // ignore
        }
      }
    }
    columnWidths.value = columns.reduce((acc, col) => {
      acc[col.key] = col.defaultWidth;
      return acc;
    }, {} as Record<string, number>);
  }

  function handleMouseDown(e: MouseEvent, columnKey: string) {
    e.preventDefault();
    resizing.value = columnKey;
    startX = e.clientX;
    startWidth = columnWidths.value[columnKey] || 100;
  }

  function handleMouseMove(e: MouseEvent) {
    if (!resizing.value) return;
    const diff = e.clientX - startX;
    const col = columns.find((c) => c.key === resizing.value);
    const minWidth = col?.minWidth || 50;
    columnWidths.value = {
      ...columnWidths.value,
      [resizing.value]: Math.max(minWidth, startWidth + diff),
    };
  }

  function handleMouseUp() {
    if (!resizing.value) return;
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(columnWidths.value));
    }
    resizing.value = null;
  }

  // Global cursor override during resize
  let cursorStyle: HTMLStyleElement | null = null;

  watch(resizing, (val) => {
    if (val) {
      cursorStyle = document.createElement('style');
      cursorStyle.textContent = '* { cursor: col-resize !important; user-select: none !important; }';
      document.head.appendChild(cursorStyle);
    } else if (cursorStyle) {
      cursorStyle.remove();
      cursorStyle = null;
    }
  });

  onMounted(() => {
    loadWidths();
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  });

  onUnmounted(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    if (cursorStyle) {
      cursorStyle.remove();
      cursorStyle = null;
    }
  });

  return { columnWidths, resizing, handleMouseDown };
}
